import React from "react";
import { FaGraduationCap, FaUser, FaLanguage, FaBriefcase, FaInfoCircle, FaMapMarkerAlt, FaPhone, FaEnvelope, FaCar, FaBirthdayCake, FaStar } from "react-icons/fa";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    phone: "Phone",
    email: "Email",
    title: "Title",
    not_specified: "Not specified",
    address: "Address",
    driver_info: "Driver information",
    diploma: "Diploma",
    birthday: "Birthday",
    experience: "Experience",
    present: "Present",
    employees: "employees",
    sector: "Sector",
    skills: "Base skills",
    activities: "Specific skills",
    accreditations: "Accreditations and diplomas",
    graduation_date: "Date of graduation",
    behavioural: "Behavioural qualities",
    individual: "Individual",
    operational: "Operational",
    relational: "Relational",
    benchmark: "Benchmark",
    languages: "Languages",
    no_diploma: "No diploma",
    cap_bep: "CAP / BEP",
    bac_pro: "Bac / Bac Pro",
    bts_dut: "BTS / DUT",
    license: "License",
    master_1: "Master 1",
    master_2: "Master 2 / Engineer",
    phd: "PhD",
    adaptability: "Adaptability",
    audacity: "Audacity",
    creativity: "Creativity",
    dynamism: "Dynamism",
    intuition: "Intuition",
    perseverance: "Perseverance",
    self_organization: "Self-organization",
    action_capacity: "Action capacity",
    cognitive_flexibility: "Cognitive flexibility",
    decision_making: "Decision making",
    initiative_capacity: "Initiative capacity",
    organization: "Organization",
    problem_solving: "Problem solving",
    sense_of_priorities: "Sense of priorities",
    contact_skills: "Contact skills",
    driving_capacity: "Driving capacity",
    framing: "Framing",
    open_listening: "Open listening",
    service_sense: "Service sense",
    team_spirit: "Team spirit",
    understanding_others: "Understanding others",
    information_pratiques: "Practical Information",
    about: "About",
  },
  fr: {
    phone: "Téléphone",
    email: "Email",
    title: "Titre",
    not_specified: "Non spécifié",
    address: "Adresse",
    driver_info: "Permis de conduire",
    diploma: "Diplôme",
    birthday: "Date de naissance",
    experience: "Expérience",
    present: "Présent",
    employees: "employés",
    sector: "Secteur",
    skills: "Compétences de base",
    activities: "Compétences spécifiques",
    accreditations: "Acréditations et diplômes",
    graduation_date: "Date de graduation",
    behavioural: "Qualités comportementales",
    individual: "Individuel",
    operational: "Opérationnel",
    relational: "Relationnel",
    benchmark: "Référence",
    languages: "Langues",
    no_diploma: "Pas de diplôme",
    cap_bep: "CAP / BEP",
    bac_pro: "Bac / Bac Pro",
    bts_dut: "BTS / DUT",
    license: "Licence",
    master_1: "Master 1",
    master_2: "Master 2 / Ingénieur",
    phd: "Doctorat",
    adaptability: "Adaptabilité",
    audacity: "Audace",
    creativity: "Créativité",
    dynamism: "Dynamisme",
    intuition: "Intuition",
    perseverance: "Persévérance",
    self_organization: "Auto-organisation",
    action_capacity: "Capacité d'action",
    cognitive_flexibility: "Flexibilité cognitive",
    decision_making: "Prise de décision",
    initiative_capacity: "Capacité d'initiative",
    organization: "Organisation",
    problem_solving: "Résolution de problèmes",
    sense_of_priorities: "Sens des priorités",
    contact_skills: "Aisance relationnelle",
    driving_capacity: "Capacité de pilotage",
    framing: "Encadrement",
    open_listening: "Écoute ouverte",
    service_sense: "Sens du service",
    team_spirit: "Esprit d'équipe",
    understanding_others: "Compréhension d'autrui",
    information_pratiques: "Informations pratiques",
    about: "À propos",
  },
};

const SectionTitle = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
    {Icon && <Icon className="text-gray-400 w-3.5 h-3.5" />}
    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">{title}</h3>
  </div>
);

const TimelineItem = ({ title, subtitle, date, description, isLast }) => (
  <div className="relative pl-6 pb-6 last:pb-0">
    {/* Line */}
    {!isLast && <div className="absolute left-[4.5px] top-2 bottom-0 w-px bg-gray-200" />}
    {/* Dot */}
    <div className="absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full border-2 border-gray-300 bg-white" />

    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1">
      <div>
        <h4 className="text-sm font-bold text-gray-900">{title}</h4>
        <p className="text-sm text-gray-600 font-medium">{subtitle}</p>
      </div>
      <span className="text-xs font-medium text-gray-400 whitespace-nowrap bg-gray-50 px-2 py-1 rounded">{date}</span>
    </div>
    {description && <p className="text-sm text-gray-500 mt-2 leading-relaxed">{description}</p>}
  </div>
);

const InfoItem = ({ icon: Icon, label, value }) => (
  <div>
    <div className="flex items-center gap-1.5 mb-1">
      {Icon && <Icon className="text-gray-400 w-3 h-3" />}
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <p className="text-sm font-medium text-gray-900 truncate" title={typeof value === "string" ? value : ""}>
      {value}
    </p>
  </div>
);

export default function InformationTab({ candidate, job }) {
  const { t } = useComponentTranslation(translations);

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
      {/* Left Column (75%) - About, Experience, Education */}
      <div className="md:col-span-3 space-y-8">
        {/* About */}
        {candidate?.about && (
          <div>
            <SectionTitle title={t("about")} icon={FaInfoCircle} />
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{candidate.about}</p>
          </div>
        )}

        {/* Experience */}
        {!!candidate?.experiences?.length && (
          <div>
            <SectionTitle title={t("experience")} icon={FaBriefcase} />
            <div className="mt-2">
              {candidate?.experiences?.map((experience, index) => (
                <TimelineItem
                  key={experience._id}
                  title={experience.title}
                  subtitle={`${experience.company || ""} ${experience.company_workforce ? `• ${experience.company_workforce} ${t("employees")}` : ""} ${experience.sector ? `• ${experience.sector}` : ""
                    }`}
                  date={`${experience.from ? new Date(experience.from).toLocaleDateString() : ""} - ${experience.to ? new Date(experience.to).toLocaleDateString() : t("present")}`}
                  isLast={index === candidate.experiences.length - 1}
                />
              ))}
            </div>
          </div>
        )}

        {/* Education / Diploma */}
        <div>
          <SectionTitle title={t("diploma")} icon={FaGraduationCap} />

          {/* Last Diploma Highlight */}
          {candidate.last_diploma && (
            <div className="mb-6 p-3 bg-blue-50/50 border border-blue-100 rounded-lg">
              <span className="text-xs text-blue-600 font-semibold uppercase tracking-wider block mb-1">{t("diploma")}</span>
              <p className="text-sm font-bold text-blue-900">{t(candidate.last_diploma)}</p>
            </div>
          )}

          <div className="mt-2">
            {!candidate?.accreditations?.length && !candidate?.education?.length && <p className="text-sm text-gray-500 italic">{t("not_specified")}</p>}

            {/* Accreditations List */}
            {candidate?.accreditations?.map((accreditation, index) => (
              <TimelineItem
                key={accreditation._id}
                title={accreditation.accreditation_name}
                subtitle={t("accreditations")}
                date={accreditation.graduation_date ? new Date(accreditation.graduation_date).toLocaleDateString() : ""}
                isLast={index === candidate.accreditations.length - 1 && !candidate?.education?.length}
              />
            ))}

            {/* Education List */}
            {candidate?.education?.map((edu, index) => (
              <TimelineItem
                key={edu._id}
                title={edu.degree}
                subtitle={`${edu.institution} ${edu.field_of_study ? `• ${edu.field_of_study}` : ""}`}
                date={`${edu.from ? new Date(edu.from).toLocaleDateString() : ""} - ${edu.to ? new Date(edu.to).toLocaleDateString() : t("present")}`}
                description={edu.description}
                isLast={index === candidate.education.length - 1}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right Sidebar (25%) - Skills & Languages */}
      <div className="md:col-span-1 space-y-8">
        {/* Skills */}
        {/* {!!candidate?.skills?.length && (
          <div>
            <SectionTitle title={t("skills")} icon={FaStar} />
            <div className="flex flex-wrap gap-2">
              {candidate.skills.slice(0, 15).map((skill) => {
                console.log(skill);
                return (
                  <span
                    key={skill._id || skill.skill_id}
                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                  >
                    {skill.skill_name || (typeof skill.name === 'object' ? skill.name?.name : skill.name)}
                  </span>
                )
              })}
              {candidate.skills.length > 15 && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                  +{candidate.skills.length - 15} more
                </span>
              )}
            </div>
          </div>
        )} */}

        {/* Languages */}
        <div>
          <SectionTitle title={t("languages")} icon={FaLanguage} />
          <div className="space-y-3">
            {candidate?.languages?.length > 0 ? (
              candidate.languages.map((language) => (
                <div key={language._id || language.isoCode} className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-900">{language.name || language.isoCode}</span>
                  <span className="text-xs px-2.5 py-1 text-gray-600 bg-gray-50 rounded font-medium">{language.level}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 italic">{t("not_specified")}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
